import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Skeleton, Tab } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroPageContainer } from '../../../shared/components';
import { useAuth } from '../../../shared/context';
import { useDataRoomUsersQuery, useDataRoomQuery } from '../../../shared/hooks';
import { AssociatedTeamMember, DataRoomUser, MilestoneReport, UnlockUser } from '../../../shared/models';
import { UnlockUserDialog } from '../UnlockUserDialog';
import { ViewAccessLogs } from '../ViewAccessLogs';
import { AddLenderNoteForm } from './AddLenderNoteForm';
import { AddTeamMemberForm } from './AddTeamMemberForm';
import { DataRoomUserList } from './DataRoomUserList';
import { DeleteDataRoomTeamMemberDialog } from './DeleteDataRoomTeamMemberDialog';
import { OptOutForm } from './OptOutForm';
import { SendInviteForm } from './SendInviteForm';
import { UploadNdaForm } from './UploadNdaForm';
import { ViewLenderNotes } from './ViewLenderNotes';
import { LenderGrantAccessDialog } from './LenderGrantAccessDialog';
import { LenderDeleteDialog } from './LenderDeleteDialog';
import { OptOutDetails } from './OptOutDetails';
import { SendEmailForm } from './SendEmailForm';
import { ViewSentEmails } from './ViewSentEmails';
import { ViewContactDetails } from './ViewContactDetails';

export const LenderParticipants: React.FC = () => {
	const auth = useAuth();
	const { guid } = useParams();
	const [tab, setTab] = React.useState('active');
	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const [userBeingOptedOut, setUserBeingOptedOut] = React.useState<DataRoomUser | null>(null);
	const [viewUserOptedOutDetails, setViewUserOptedOutDetails] = React.useState<DataRoomUser | null>(null);
	const [userBeingUnlocked, setUserBeingUnlocked] = React.useState<UnlockUser | null>(null);
	const [viewAccessLogs, setViewAccessLogs] = React.useState<UnlockUser | null>(null);
	const [notesBeingViewed, setNotesBeingViewed] = React.useState<DataRoomUser | null>(null);
	const [noteBeingAdded, setNoteBeingAdded] = React.useState<DataRoomUser | null>(null);
	const [teamMemberBeingAdded, setTeamMemberBeingAdded] = React.useState<DataRoomUser | null>(null);
	const [userBeingInvited, setUserBeingInvited] = React.useState<UnlockUser | null>(null);
	const [sendEmail, setSendEmail] = React.useState<DataRoomUser | null>(null);
	const [viewEmails, setViewEmails] = React.useState<DataRoomUser | null>(null);
	const [userUploadedNda, setUserUploadingNda] = React.useState<DataRoomUser | null>(null);
	const [userBeingGranted, setUserBeingGranted] = React.useState<DataRoomUser | null>(null);
	const [dataRoomUserBeingDeleted, setDataRoomUserBeingDeleted] = React.useState<DataRoomUser | null>(null);

	const [teamMemberBeingRemoved, setTeamMemberBeingRemoved] = React.useState<AssociatedTeamMember | null>(null);
	const [viewContactDetails, setViewContactDetails] = React.useState<DataRoomUser | null>(null);
	const [viewTeamMemberDetails, setViewTeamMemberDetails] = React.useState<AssociatedTeamMember | null>(null);
	const [milestoneDetail, setMilestoneDetail] = React.useState<MilestoneReport | null>(null);

	const clearDialogsAndDrawers = (): void => {
		setUserBeingOptedOut(null);
		setUserBeingUnlocked(null);
		setViewAccessLogs(null);
		setNotesBeingViewed(null);
		setNoteBeingAdded(null);
		setUserBeingInvited(null);
		setSendEmail(null);
		setViewEmails(null);
		setUserUploadingNda(null);
		setTeamMemberBeingAdded(null);
		setTeamMemberBeingRemoved(null);
		setUserBeingGranted(null);
		setDataRoomUserBeingDeleted(null);
		setViewUserOptedOutDetails(null);
		setViewContactDetails(null);
		setViewTeamMemberDetails(null);
		setMilestoneDetail(null);
	};

	const dataRoomQuery = useDataRoomQuery(guid);
	const dataRoomUserQuery = useDataRoomUsersQuery(guid);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Participants &gt; Lenders</CerebroBreadcrumb>

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Active" value="active" />
						<Tab label="Inactive" value="inactive" />
						<Tab label="All" value="all" />
					</TabList>
				</Box>

				{(dataRoomUserQuery.isLoading || dataRoomQuery.isLoading) && (
					<>
						<Skeleton height={200} />
						<Skeleton height={200} />
						<Skeleton height={200} />
					</>
				)}

				{dataRoomUserQuery.isSuccess && dataRoomQuery.isSuccess && (
					<>
						<TabPanel value="active">
							<DataRoomUserList
								dataRoomGuid={guid}
								currentUser={auth.currentUser}
								dataRoom={dataRoomQuery.data}
								users={dataRoomUserQuery.data.filter(user => user.dealBankStatus == 'active')}
								tab="active"
								unlockUser={setUserBeingUnlocked}
								accessLogs={setViewAccessLogs}
								optOutUser={setUserBeingOptedOut}
								addNote={setNoteBeingAdded}
								viewNotes={setNotesBeingViewed}
								addTeamMember={setTeamMemberBeingAdded}
								removeTeamMember={setTeamMemberBeingRemoved}
								sendInvite={setUserBeingInvited}
								sendEmail={setSendEmail}
								viewEmails={setViewEmails}
								uploadNda={setUserUploadingNda}
								grantAccess={setUserBeingGranted}
								deleteDataRoomUser={setDataRoomUserBeingDeleted}
								viewOptOut={setViewUserOptedOutDetails}
								contactDetails={setViewContactDetails}
								teamMemberDetails={setViewTeamMemberDetails}
								progressDetails={setMilestoneDetail}
							/>
						</TabPanel>
						<TabPanel value="inactive">
							<DataRoomUserList
								dataRoomGuid={guid}
								currentUser={auth.currentUser}
								dataRoom={dataRoomQuery.data}
								users={dataRoomUserQuery.data.filter(user => user.dealBankStatus == 'inactive')}
								tab="inactive"
								unlockUser={setUserBeingUnlocked}
								accessLogs={setViewAccessLogs}
								optOutUser={setUserBeingOptedOut}
								addNote={setNoteBeingAdded}
								viewNotes={setNotesBeingViewed}
								addTeamMember={setTeamMemberBeingAdded}
								removeTeamMember={setTeamMemberBeingRemoved}
								sendInvite={setUserBeingInvited}
								sendEmail={setSendEmail}
								viewEmails={setViewEmails}
								uploadNda={setUserUploadingNda}
								grantAccess={setUserBeingGranted}
								deleteDataRoomUser={setDataRoomUserBeingDeleted}
								viewOptOut={setViewUserOptedOutDetails}
								contactDetails={setViewContactDetails}
								teamMemberDetails={setViewTeamMemberDetails}
								progressDetails={setMilestoneDetail}
							/>
						</TabPanel>
						<TabPanel value="all">
							<DataRoomUserList
								dataRoomGuid={guid}
								currentUser={auth.currentUser}
								dataRoom={dataRoomQuery.data}
								users={dataRoomUserQuery.data}
								tab="all"
								unlockUser={setUserBeingUnlocked}
								accessLogs={setViewAccessLogs}
								optOutUser={setUserBeingOptedOut}
								addNote={setNoteBeingAdded}
								viewNotes={setNotesBeingViewed}
								addTeamMember={setTeamMemberBeingAdded}
								removeTeamMember={setTeamMemberBeingRemoved}
								sendInvite={setUserBeingInvited}
								sendEmail={setSendEmail}
								viewEmails={setViewEmails}
								uploadNda={setUserUploadingNda}
								grantAccess={setUserBeingGranted}
								deleteDataRoomUser={setDataRoomUserBeingDeleted}
								viewOptOut={setViewUserOptedOutDetails}
								contactDetails={setViewContactDetails}
								teamMemberDetails={setViewTeamMemberDetails}
								progressDetails={setMilestoneDetail}
							/>
						</TabPanel>
					</>
				)}
			</TabContext>

			<OptOutForm dataRoomGuid={guid} user={userBeingOptedOut} close={clearDialogsAndDrawers} />
			<OptOutDetails dataRoomGuid={guid} user={viewUserOptedOutDetails} close={clearDialogsAndDrawers} />
			<UnlockUserDialog dataRoomGuid={guid} user={userBeingUnlocked} close={clearDialogsAndDrawers} />
			<ViewAccessLogs dataRoomGuid={guid} user={viewAccessLogs} close={clearDialogsAndDrawers} />
			<ViewLenderNotes dataRoomGuid={guid} user={notesBeingViewed} close={clearDialogsAndDrawers} />
			<AddTeamMemberForm
				dataRoomGuid={guid}
				user={teamMemberBeingAdded}
				isBankUser={auth.currentUser.isBankUser}
				close={clearDialogsAndDrawers}
			/>
			<SendInviteForm dataRoomGuid={guid} user={userBeingInvited} close={clearDialogsAndDrawers} />
			<SendEmailForm dataRoomGuid={guid} user={sendEmail} close={clearDialogsAndDrawers} />
			<ViewSentEmails dataRoomGuid={guid} user={viewEmails} close={clearDialogsAndDrawers} />
			<UploadNdaForm dataRoomGuid={guid} user={userUploadedNda} close={clearDialogsAndDrawers} />
			<LenderGrantAccessDialog dataRoomGuid={guid} user={userBeingGranted} close={clearDialogsAndDrawers} />
			<LenderDeleteDialog dataRoomGuid={guid} user={dataRoomUserBeingDeleted} close={clearDialogsAndDrawers} />

			<ViewContactDetails roomGuid={guid} roomUser={viewContactDetails} close={clearDialogsAndDrawers} />
			<ViewContactDetails roomGuid={guid} teamMember={viewTeamMemberDetails} close={clearDialogsAndDrawers} />

			<AddLenderNoteForm
				dataRoomGuid={guid}
				user={noteBeingAdded}
				currentUser={auth.currentUser}
				close={clearDialogsAndDrawers}
			/>

			<DeleteDataRoomTeamMemberDialog
				dataRoomGuid={guid}
				member={teamMemberBeingRemoved}
				close={clearDialogsAndDrawers}
			/>
		</CerebroPageContainer>
	);
};
